import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { PriorityBadge, StatusBadge, CategoryBadge } from "../components/Badges";
import AttachmentPanel from "../components/AttachmentPanel";
import RichEditor from "../components/RichEditor";
import RichView from "../components/RichView";
import TranslateFlags from "../components/TranslateFlags";
import UserInfoPopover from "../components/UserInfoPopover";
import { PRIORITIES, PRIORITY_LABEL, STATUSES, STATUS_LABEL, CATEGORIES, CATEGORY_LABEL, relTime, absTime, slaCountdown } from "../lib/labels";
import {
  Clock, ArrowLeft, PaperPlaneTilt, Sparkle, Lock, Timer, User as UserIcon, ClockCounterClockwise, Lightning, HardDrives,
  Envelope as EnvelopeIcon, Eye, FilePdf, Phone,
} from "@phosphor-icons/react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel } from "../components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { CalendarBlank, X } from "@phosphor-icons/react";
import ConfirmDialog from "../components/ConfirmDialog";

// v266 — CommentRow subcomponent. v262 avait ajouté un TranslateFlags par
// commentaire mais l'user a demandé de les retirer : « pas besoin de mettre
// les drapeaux sur les messages déjà traité car ils sont figé ». Les
// drapeaux ne restent que sur la description ticket + le composer.
function CommentRow({ c, isStaff }) {
  const [translated, setTranslated] = useState(null);
  const isClient = c.author_role === "client";
  return (
    <div
      data-testid={`comment-${c.id}`}
      className={`rounded-lg border p-4 ${c.internal ? "bg-orange-50/60 border-orange-200 dark:bg-orange-950/20 dark:border-orange-900" : "bg-muted/30"}`}
    >
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2 flex-wrap">
        <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[0.65rem] font-semibold">
          {c.author_name?.[0]}
        </span>
        <span className="font-semibold text-foreground">{c.author_name}</span>
        <span className="uppercase tracking-wider">{c.author_role}</span>
        <span>·</span>
        <span title={absTime(c.created_at)}>{relTime(c.created_at)}</span>
        <span className="text-muted-foreground" data-testid={`comment-abs-${c.id}`}>· {absTime(c.created_at)}</span>
        {isStaff && isClient && (
          <span className="ml-1" data-testid={`translate-flags-comment-${c.id}`}>
            <TranslateFlags
              text={c.body}
              onTranslated={setTranslated}
              onRestore={() => setTranslated(null)}
              compact
            />
          </span>
        )}
        {c.internal && (
          <span className="ml-auto inline-flex items-center gap-1 text-orange-600 dark:text-orange-400">
            <Lock size={12}/> Interne
          </span>
        )}
      </div>
      <RichView html={translated ?? c.body} className="text-sm"/>
    </div>
  );
}

export default function TicketDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isStaff = user?.role === "admin" || user?.role === "technicien";
  const [ticket, setTicket] = useState(null);
  const [comment, setComment] = useState("");
  // v262 — Ephemeral translations (never persisted). Description-only for now;
  // per-comment translation is handled inline via each row's own state.
  const [translatedDescription, setTranslatedDescription] = useState(null);
  // v264 — Detected client language (from ticket description). Populated
  // once on load if the module has `auto_detect_client_lang` enabled.
  const [detectedLang, setDetectedLang] = useState(null);
  const [internal, setInternal] = useState(false);
  const [techs, setTechs] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);
  const [macros, setMacros] = useState([]);
  const [emailEvents, setEmailEvents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [clientPopoverOpen, setClientPopoverOpen] = useState(false);
  const [ai, setAi] = useState(false);
  const [scheduled, setScheduled] = useState([]);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  const [cancelSc, setCancelSc] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const editorRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/tickets/${id}`);
      setTicket(r.data);
      setLoadError(null);
      if (isStaff && r.data?.client_id) {
        api.get("/equipment", { params: { client_id: r.data.client_id } }).then((x) => setEquipmentList(x.data));
        api.get(`/tickets/${id}/email-events`).then((x) => setEmailEvents(x.data)).catch(() => {});
        api.get(`/tickets/${id}/scheduled-comments`).then((x) => setScheduled(x.data)).catch(() => {});
      }
    } catch (e) {
      const code = e?.response?.status;
      let msg = "Impossible de charger ce ticket.";
      if (code === 403) msg = "Vous n'avez pas accès à ce ticket. Vérifiez que vous êtes connecté(e) avec la bonne adresse email.";
      else if (code === 404) msg = "Ce ticket n'existe pas ou a été supprimé.";
      else if (code === 401) msg = "Votre session a expiré. Veuillez vous reconnecter.";
      setLoadError({ code, msg });
    }
  };

  useEffect(() => {
    load();
    if (isStaff) {
      api.get("/users", { params: { role: "technicien", active: true } }).then((r) => setTechs(r.data));
      api.get("/macros").then((r) => setMacros(r.data));
      // Signature is NOT pre-filled inside the TipTap editor anymore:
      //  1) StarterKit strips top-level <div>, dropping the border-top/margin separator
      //     → the message ended up glued to the signature even with line breaks.
      //  2) The server (send_email) already appends BOTH the tech's personal signature
      //     AND the company signature automatically → prefilling caused duplicates.
      // A discreet hint under the composer tells the tech what will be added.
    }
  // eslint-disable-next-line
  }, [id]);

  // v264 — Detect client language once the ticket is loaded. Silent-fail if
  // the module is disabled or auto-detect is off (backend returns 403).
  useEffect(() => {
    if (!isStaff || !ticket?.description) return;
    let cancelled = false;
    api.get("/translate/languages")
      .then((r) => {
        if (cancelled || !r.data.enabled || !r.data.auto_detect_client_lang) return;
        return api.post("/translate/detect", { text: ticket.description });
      })
      .then((r) => { if (!cancelled && r?.data?.lang) setDetectedLang(r.data.lang); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [ticket?.id, ticket?.description, isStaff]);

  if (loadError) {
    const backTo = user?.role === "client" ? "/mes-tickets" : "/tickets";
    return (
      <div className="max-w-lg mx-auto text-center py-12" data-testid="ticket-load-error">
        <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/30 p-6">
          <div className="font-display text-xl font-semibold mb-2">Ticket inaccessible</div>
          <p className="text-sm text-muted-foreground mb-4">{loadError.msg}</p>
          <Link to={backTo} className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
            <ArrowLeft size={14}/> Retour à mes tickets
          </Link>
        </div>
      </div>
    );
  }
  if (!ticket) return <div className="text-muted-foreground">Chargement…</div>;

  const sla = slaCountdown(ticket.sla_resolution_deadline);
  const slaR = slaCountdown(ticket.sla_response_deadline);

  const update = async (patch) => {
    try {
      const r = await api.patch(`/tickets/${id}`, patch);
      setTicket(r.data);
      toast.success("Ticket mis à jour");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const submitComment = async () => {
    // strip empty HTML like "<p></p>"
    const cleanTxt = comment.replace(/<[^>]+>/g, "").trim();
    if (!cleanTxt) return;
    setBusy(true);
    try {
      const r = await api.post(`/tickets/${id}/comments`, { body: comment, internal });
      setTicket(r.data);
      setComment(""); setInternal(false);
      toast.success("Commentaire ajouté");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const sendWhatsApp = async () => {
    const cleanTxt = comment.replace(/<[^>]+>/g, "").trim();
    if (!cleanTxt) return toast.error("Écrivez d'abord votre message");
    if (!window.confirm(`Envoyer ce message via WhatsApp à ${ticket?.client_name || "le client"} ?\n(Le message sera aussi ajouté au ticket comme commentaire public.)`)) return;
    setBusy(true);
    try {
      const r = await api.post("/whatsapp/send", { ticket_id: id, body: cleanTxt, also_comment: true });
      toast.success(r.data.mock ? "[MOCK] WhatsApp simulé — configurez Twilio pour envoyer en réel" : "WhatsApp envoyé ✅");
      const t = await api.get(`/tickets/${id}`);
      setTicket(t.data);
      setComment("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const submitScheduled = async () => {
    const cleanTxt = comment.replace(/<[^>]+>/g, "").trim();
    if (!cleanTxt) return toast.error("Le message est vide");
    if (!scheduleAt) return toast.error("Choisissez une date/heure");
    // datetime-local returns "YYYY-MM-DDTHH:MM" in local time; append Z? No, treat as local, backend receives naive → UTC.
    // We convert local → ISO with timezone offset so the backend interprets correctly.
    const localDt = new Date(scheduleAt);
    if (isNaN(localDt.getTime())) return toast.error("Date invalide");
    if (localDt.getTime() <= Date.now() + 30_000) return toast.error("Choisissez au moins 1 minute dans le futur");
    setBusy(true);
    try {
      await api.post(`/tickets/${id}/schedule-comment`, {
        body: comment, internal, scheduled_for: localDt.toISOString(),
      });
      const list = await api.get(`/tickets/${id}/scheduled-comments`);
      setScheduled(list.data);
      setComment(""); setInternal(false); setScheduleAt(""); setScheduleOpen(false);
      toast.success("Réponse programmée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur programmation");
    } finally { setBusy(false); }
  };

  const doCancelScheduled = async () => {
    if (!cancelSc) return;
    try {
      await api.delete(`/scheduled-comments/${cancelSc.id}`);
      setScheduled((s) => s.filter((x) => x.id !== cancelSc.id));
      toast.success("Programmation annulée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setCancelSc(null); }
  };

  // Default schedule value = now + 1 hour, rounded down to the minute (used as min & initial)
  const defaultScheduleValue = () => {
    const d = new Date(Date.now() + 60 * 60 * 1000);
    d.setSeconds(0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const suggestAi = async () => {
    setAi(true);
    try {
      const r = await api.post(`/ai/suggest-reply/${id}`);
      const html = (r.data.reply || "")
        .split(/\n\n+/)
        .map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`).join("");
      setComment(html);
      toast.success("Suggestion IA prête");
    } catch (e) {
      toast.error(e.response?.data?.detail || "IA indisponible");
    } finally { setAi(false); }
  };

  const insertMacro = async (m) => {
    let body = m.body || "";
    body = body
      .replaceAll("{client_name}", ticket?.client_name || "")
      .replaceAll("{ticket_number}", `#${ticket?.number}`)
      .replaceAll("{ticket_subject}", ticket?.subject || "");
    const html = body.split(/\n\n+/).map((p) => `<p>${p.replace(/\n/g,"<br/>")}</p>`).join("");
    // Insert at the caret position (or beginning if editor empty) instead of appending.
    if (editorRef.current?.insertContent) {
      editorRef.current.insertContent(html);
    } else {
      setComment(comment ? `${comment}${html}` : html);
    }
    try { await api.post(`/macros/${m.id}/use`); } catch { /* noop */ }
    // If the macro has attachments, clone them into this ticket
    const attCount = (m.attachments || []).length;
    if (attCount > 0) {
      try {
        const r = await api.post(`/tickets/${id}/macros/${m.id}/attach`);
        const added = (r.data?.added || []).length;
        if (added > 0) {
          toast.success(`Macro « ${m.name} » insérée · ${added} pièce${added > 1 ? "s" : ""} jointe${added > 1 ? "s" : ""} ajoutée${added > 1 ? "s" : ""}`);
          load();
          return;
        }
      } catch { /* noop */ }
    }
    toast.success(`Macro « ${m.name} » insérée`);
  };

  const visibleComments = ticket.comments || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to={user?.role === "client" ? "/mes-tickets" : "/tickets"} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="back-link">
          <ArrowLeft size={16}/> Retour
        </Link>
        <span className="text-muted-foreground">/</span>
        <span className="mono text-muted-foreground">#{ticket.number}</span>
        <div className="ml-auto">
          <Button
            variant="secondary" size="sm"
            data-testid="ticket-pdf-btn"
            onClick={async () => {
              // v274 — Simplified flow. The old per-comment prompt loop was
              // unusable past 5 comments AND opening the PDF in a new tab after
              // `await` was silently blocked by popup blockers. Now: single
              // optional note prompt + anchor-download (no popup involved).
              const note = window.prompt("Note pour le client à ajouter en fin de rapport (facultatif, laissez vide pour aucun)", "") ?? "";
              const body = { comment_ids: [], client_note: note.trim() };
              const t = toast.loading("Génération du rapport PDF…");
              try {
                const r = await api.post(`/tickets/${id}/pdf`, body, { responseType: "blob" });
                const blobUrl = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                const a = document.createElement("a");
                a.href = blobUrl;
                a.download = `Ticket_${ticket.number || id}.pdf`;
                a.rel = "noopener";
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
                toast.success("Rapport PDF téléchargé", { id: t });
              } catch (e) {
                toast.error("Impossible de générer le PDF : " + (e?.response?.data?.detail || e.message || "erreur inconnue"), { id: t });
              }
            }}
          >
            <FilePdf size={14} className="mr-1.5"/> Rapport PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <PriorityBadge value={ticket.priority}/>
                    <CategoryBadge value={ticket.category}/>
                    <StatusBadge value={ticket.status}/>
                    {isStaff && (() => {
                      // Last email sent to the CLIENT (recipient == client email). Show a small
                      // "vu / non vu" pill so the tech knows immediately if the client has seen the reply.
                      const clientMail = (ticket.client_email || "").toLowerCase();
                      const toClient = emailEvents.filter((ev) => (ev.recipient || "").toLowerCase() === clientMail);
                      const last = toClient[toClient.length - 1];
                      if (!last) return null;
                      return last.opened_at ? (
                        <span
                          data-testid="client-read-pill"
                          title={`Ouvert ${last.open_count || 1}× · première ouverture ${absTime(last.opened_at)}${last.last_open_at && last.last_open_at !== last.opened_at ? ` · dernière ${absTime(last.last_open_at)}` : ""}`}
                          className="inline-flex items-center gap-1 rounded-full bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wider"
                        >
                          <Eye size={10}/> Vu {absTime(last.opened_at)}
                        </span>
                      ) : (
                        <span
                          data-testid="client-unread-pill"
                          title={`Envoyé ${absTime(last.sent_at)} — pas encore ouvert par le client`}
                          className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wider"
                        >
                          <Eye size={10}/> Non ouvert
                        </span>
                      );
                    })()}
                    {isStaff && (
                      <span className="inline-flex" data-testid="translate-flags-description">
                        <TranslateFlags
                          text={ticket.description}
                          onTranslated={(t) => setTranslatedDescription(t)}
                          onRestore={() => setTranslatedDescription(null)}
                          recommendedLang={detectedLang}
                          compact
                        />
                      </span>
                    )}
                  </div>
                  <CardTitle className="font-display text-2xl leading-snug">{ticket.subject}</CardTitle>
                  <div className="text-sm text-muted-foreground mt-1">
                    Ouvert par <b>{ticket.client_name}</b> · {ticket.client_email} · <span title={absTime(ticket.created_at)}>{relTime(ticket.created_at)}</span> <span data-testid="ticket-created-abs">({absTime(ticket.created_at)})</span> · <span className="uppercase text-xs">{ticket.source}</span>
                    {isStaff && (
                      <Link
                        to={`/tickets?client_id=${ticket.client_id}`}
                        data-testid="client-history-link"
                        className="ml-2 text-primary hover:underline inline-flex items-center gap-1 text-xs"
                      >
                        <ClockCounterClockwise size={12}/> Historique du client
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap leading-relaxed">
                <RichView html={translatedDescription ?? ticket.description}/>
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="font-display text-lg">Conversation ({visibleComments.length})</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {visibleComments.length === 0 && (
                <p className="text-sm text-muted-foreground">Aucun échange pour le moment.</p>
              )}
              {visibleComments.map((c) => (
                <CommentRow key={c.id} c={c} isStaff={isStaff} />
              ))}
              {/* /v262 end wrapped block */}

              <div className="border-t pt-4 space-y-3">
                {isStaff && scheduled.length > 0 && (
                  <div className="rounded-md border border-dashed bg-muted/30 px-3 py-2 space-y-1" data-testid="scheduled-list">
                    <div className="text-[0.7rem] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                      <CalendarBlank size={11}/> Réponses programmées ({scheduled.length})
                    </div>
                    {scheduled.map((sc) => (
                      <div key={sc.id} className="flex items-center gap-2 text-xs" data-testid={`scheduled-${sc.id}`}>
                        <span className="mono text-muted-foreground">
                          {new Date(sc.scheduled_for).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </span>
                        {sc.internal && <span className="text-orange-600">· interne</span>}
                        <span className="text-muted-foreground truncate flex-1">
                          {sc.body.replace(/<[^>]+>/g, "").slice(0, 60)}…
                        </span>
                        <button
                          type="button"
                          className="text-red-600 hover:text-red-700 p-1 rounded hover:bg-red-50 dark:hover:bg-red-950/30"
                          onClick={() => setCancelSc(sc)}
                          data-testid={`cancel-scheduled-${sc.id}`}
                          title="Annuler la programmation"
                        >
                          <X size={12}/>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {isStaff && (
                  <div className="flex items-center gap-2 flex-wrap" data-testid="translate-flags-composer">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      Traduire ma réponse
                      {detectedLang && (
                        <span className="ml-1 text-emerald-600 dark:text-emerald-400 normal-case font-semibold" data-testid="detected-lang-hint">
                          · client détecté : {detectedLang.toUpperCase()}
                        </span>
                      )}
                    </span>
                    <TranslateFlags
                      getValue={() => comment}
                      setValue={setComment}
                      recommendedLang={detectedLang}
                      compact
                    />
                  </div>
                )}
                <RichEditor
                  ref={editorRef}
                  value={comment}
                  onChange={setComment}
                  attachTicketId={ticket.id}
                  placeholder={isStaff ? "Répondre au client ou noter une remarque interne…" : "Ajouter un message pour l'équipe support…"}
                  minHeight={160}
                />
                {isStaff && !internal && (
                  <div
                    data-testid="signature-auto-hint"
                    className="text-xs text-muted-foreground italic px-1 -mt-1 flex items-center gap-1.5"
                  >
                    <span className="inline-block w-1 h-1 rounded-full bg-emerald-500"/>
                    Votre signature personnelle et la signature de l'entreprise seront ajoutées automatiquement à l'envoi.
                  </div>
                )}
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-4">
                    {isStaff && (
                      <div className="flex items-center gap-2">
                        <Switch id="internal" data-testid="comment-internal-switch" checked={internal} onCheckedChange={setInternal}/>
                        <Label htmlFor="internal" className="text-sm">Note interne (invisible pour le client)</Label>
                      </div>
                    )}
                    {isStaff && (
                      <Button data-testid="ai-suggest-btn" variant="secondary" size="sm" onClick={suggestAi} disabled={ai}>
                        <Sparkle size={14} className="mr-2"/> {ai ? "…" : "Suggérer une réponse IA"}
                      </Button>
                    )}
                    {isStaff && macros.length > 0 && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button data-testid="macro-menu" variant="secondary" size="sm">
                            <Lightning size={14} className="mr-2"/> Macros ({macros.length})
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="max-h-80 overflow-y-auto w-72">
                          <DropdownMenuLabel>Insérer une réponse enregistrée</DropdownMenuLabel>
                          {macros.map((m) => (
                            <DropdownMenuItem key={m.id} onSelect={() => insertMacro(m)} data-testid={`insert-macro-${m.id}`}>
                              <div className="flex flex-col">
                                <span className="font-medium">{m.name}</span>
                                <span className="text-xs text-muted-foreground truncate max-w-[220px]">{m.body.slice(0, 60)}…</span>
                              </div>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {isStaff && (
                      <Popover open={scheduleOpen} onOpenChange={(v) => { setScheduleOpen(v); if (v && !scheduleAt) setScheduleAt(defaultScheduleValue()); }}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="ghost" size="sm"
                            data-testid="schedule-toggle"
                            disabled={busy || !comment.replace(/<[^>]+>/g, "").trim()}
                            title="Programmer l&apos;envoi"
                            className="text-muted-foreground hover:text-foreground"
                          >
                            <Clock size={16}/>
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="w-72">
                          <div className="space-y-3">
                            <div>
                              <div className="text-sm font-medium">Programmer l&apos;envoi</div>
                              <div className="text-xs text-muted-foreground">Le message sera publié automatiquement à l&apos;heure indiquée.</div>
                            </div>
                            <div>
                              <Label htmlFor="sched-dt" className="text-xs">Date & heure</Label>
                              <input
                                id="sched-dt"
                                type="datetime-local"
                                data-testid="schedule-datetime"
                                className="mt-1 w-full h-9 rounded-md border bg-background px-3 text-sm"
                                value={scheduleAt || defaultScheduleValue()}
                                min={defaultScheduleValue()}
                                onChange={(e) => setScheduleAt(e.target.value)}
                              />
                            </div>
                            <div className="flex justify-end gap-2">
                              <Button variant="ghost" size="sm" onClick={() => setScheduleOpen(false)}>Annuler</Button>
                              <Button size="sm" data-testid="schedule-confirm" onClick={submitScheduled} disabled={busy}>
                                <CalendarBlank size={14} className="mr-1.5"/> Programmer
                              </Button>
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    )}
                    <Button data-testid="comment-submit" onClick={submitComment} disabled={busy || !comment.trim()}>
                      <PaperPlaneTilt size={14} className="mr-2"/> {busy ? "Envoi…" : "Envoyer"}
                    </Button>
                    <Button variant="outline" data-testid="whatsapp-send" onClick={sendWhatsApp} disabled={busy || !comment.trim()}
                            className="text-green-700 border-green-300 hover:bg-green-50 dark:text-green-400 dark:border-green-800 dark:hover:bg-green-950"
                            title="Envoyer ce message via WhatsApp au client">
                      📱 WhatsApp
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <ConfirmDialog
            open={!!cancelSc}
            onOpenChange={(v) => !v && setCancelSc(null)}
            title="Annuler la programmation ?"
            description={cancelSc ? `Le message prévu pour le ${new Date(cancelSc.scheduled_for).toLocaleString("fr-FR")} ne sera pas envoyé.` : ""}
            confirmLabel="Annuler la programmation"
            variant="danger"
            onConfirm={doCancelScheduled}
          />

          <Card>
            <CardHeader><CardTitle className="font-display text-lg flex items-center gap-2"><ClockCounterClockwise size={18}/> Historique</CardTitle></CardHeader>
            <CardContent>
              <ol className="relative border-l pl-6 space-y-4">
                {(ticket.history || []).map((h) => (
                  <li key={h.id} className="text-sm">
                    <span className="absolute -left-[5px] w-2.5 h-2.5 rounded-full bg-primary"/>
                    <div>
                      <span className="font-medium">{labelForAction(h.action)}</span>
                      {h.from_value && <> — de <span className="mono">{h.from_value}</span></>}
                      {h.to_value && <> → <span className="mono">{h.to_value}</span></>}
                    </div>
                    <div className="text-xs text-muted-foreground">{h.author_name || "système"} · <span title={absTime(h.at)}>{relTime(h.at)}</span> <span data-testid={`history-abs-${h.at || ""}`}>({absTime(h.at)})</span></div>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle className="font-display text-lg">Détails</CardTitle></CardHeader>
            <CardContent className="space-y-4 text-sm">
              <Row label="Client" value={
                <div>
                  {ticket.client_id ? (
                    <button
                      type="button"
                      onClick={() => setClientPopoverOpen(true)}
                      className="font-medium hover:underline text-left"
                      data-testid="ticket-client-name-btn"
                      title="Voir la fiche client"
                    >
                      {ticket.client_name}
                    </button>
                  ) : (
                    <div className="font-medium">{ticket.client_name}</div>
                  )}
                  <a href={`mailto:${ticket.client_email}`} className="text-xs text-primary hover:underline block">{ticket.client_email}</a>
                  {ticket.client_phone ? (
                    <a href={`tel:${(ticket.client_phone || "").replace(/\s+/g, "")}`}
                       data-testid="ticket-client-phone"
                       className="text-xs text-emerald-700 dark:text-emerald-400 hover:underline mono inline-flex items-center gap-1 mt-0.5">
                      <Phone size={12} weight="fill"/>{ticket.client_phone}
                    </a>
                  ) : (
                    <div className="text-xs text-muted-foreground italic mt-0.5">Téléphone non renseigné</div>
                  )}
                  {ticket.client_company && (
                    <div className="text-xs text-muted-foreground mt-0.5">{ticket.client_company}</div>
                  )}
                </div>
              } icon={<UserIcon size={14}/>}/>
              <Row label="SLA prise en charge" value={<span className={`mono ${slaR.breached ? "text-destructive" : slaR.urgent ? "text-orange-500" : ""}`}><Clock size={12} className="inline mr-1"/>{slaR.text}</span>}/>
              <Row label="SLA résolution" value={<span className={`mono ${sla.breached ? "text-destructive" : sla.urgent ? "text-orange-500" : ""}`}><Timer size={12} className="inline mr-1"/>{sla.text}</span>}/>
              <Row label="Créé le" value={new Date(ticket.created_at).toLocaleString("fr-FR")}/>
              {ticket.resolved_at && <Row label="Résolu le" value={new Date(ticket.resolved_at).toLocaleString("fr-FR")}/>}
              {ticket.satisfaction_score && (
                <Row label="Satisfaction client" value={<span className="font-semibold text-emerald-600">{ticket.satisfaction_score}/5 ⭐</span>}/>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <AttachmentPanel
                ticketId={ticket.id}
                attachments={ticket.attachments || []}
                onChange={() => load()}
                currentUserId={user?.id}
                currentUserRole={user?.role}
              />
            </CardContent>
          </Card>

          {isStaff && emailEvents.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="font-display text-lg flex items-center gap-2"><EnvelopeIcon size={16}/> Suivi e-mails ({emailEvents.length})</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {emailEvents.map((ev, i) => {
                  const status = ev.delivery_status || "delivered";  // legacy events default to delivered
                  const statusBadge = status === "delivered"
                    ? <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded text-[0.65rem] font-semibold uppercase tracking-wider">Livré</span>
                    : status === "pending"
                    ? <span className="inline-flex items-center gap-1 text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded text-[0.65rem] font-semibold uppercase tracking-wider animate-pulse">En cours</span>
                    : <span className="inline-flex items-center gap-1 text-destructive bg-destructive/10 px-1.5 py-0.5 rounded text-[0.65rem] font-semibold uppercase tracking-wider" title={ev.last_error || ""}>Échec</span>;
                  return (
                  <div key={i} className="text-xs flex items-start justify-between gap-3 border-b pb-2 last:border-b-0" data-testid={`email-ev-${i}`}>
                    <div className="min-w-0">
                      <div className="font-medium truncate flex items-center gap-2">{ev.subject} {statusBadge}</div>
                      <div className="text-muted-foreground">
                        {ev.recipient} · envoyé {relTime(ev.sent_at)}
                        {ev.retry_count > 0 && <span className="ml-1.5 text-orange-600">· {ev.retry_count} retry</span>}
                      </div>
                      {status === "failed" && ev.last_error && (
                        <div className="text-destructive/80 truncate mt-0.5" title={ev.last_error}>⚠ {ev.last_error}</div>
                      )}
                    </div>
                    <div className="shrink-0 text-right">
                      {ev.opened_at ? (
                        <div className="flex flex-col items-end gap-0.5">
                          <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold" title={`Première ouverture : ${absTime(ev.opened_at)}${ev.last_open_at && ev.last_open_at !== ev.opened_at ? `\nDernière ouverture : ${absTime(ev.last_open_at)}` : ""}`}>
                            <Eye size={11}/> ouvert {ev.open_count || 1}×
                          </span>
                          <span className="text-muted-foreground/70 mono text-[0.65rem]" data-testid={`email-ev-opened-${i}`}>
                            {absTime(ev.opened_at)}
                          </span>
                        </div>
                      ) : status === "delivered" ? (
                        <span className="text-muted-foreground" title={`Envoyé ${absTime(ev.sent_at)} — pas encore ouvert`}>non ouvert</span>
                      ) : null}
                    </div>
                  </div>
                );})}
              </CardContent>
            </Card>
          )}

          {isStaff && (
            <Card>
              <CardHeader><CardTitle className="font-display text-lg">Actions</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <Field label="Statut">
                  <Select value={ticket.status} onValueChange={(v) => update({ status: v })}>
                    <SelectTrigger data-testid="status-select"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Priorité">
                  <Select value={ticket.priority} onValueChange={(v) => update({ priority: v })}>
                    <SelectTrigger data-testid="priority-select"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{PRIORITY_LABEL[p]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Catégorie">
                  <Select value={ticket.category} onValueChange={(v) => update({ category: v })}>
                    <SelectTrigger data-testid="category-select"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABEL[c]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Assigné à">
                  <Select value={ticket.assignee_id || "__none"} onValueChange={(v) => update({ assignee_id: v === "__none" ? null : v })}>
                    <SelectTrigger data-testid="assignee-select"><SelectValue placeholder="Non assigné"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">Non assigné</SelectItem>
                      {/* v296 — Si l'assignee actuel n'est plus dans la liste des techs actifs
                           (compte suspendu / archivé après assignation), on le ré-injecte comme
                           option désactivée pour que son nom reste visible, mais plus sélectionnable. */}
                      {ticket.assignee_id && !techs.some((t) => t.id === ticket.assignee_id) && (
                        <SelectItem value={ticket.assignee_id} disabled data-testid="assignee-suspended-option">
                          {ticket.assignee_name || "—"} <span className="text-xs text-amber-600">(suspendu)</span>
                        </SelectItem>
                      )}
                      {techs.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`assignee-opt-${t.id}`}>{t.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Temps passé (min)">
                  <input
                    data-testid="time-spent"
                    type="number" min={0}
                    className="w-full border rounded-md h-9 px-3 text-sm bg-background"
                    defaultValue={ticket.time_spent_minutes || 0}
                    onBlur={(e) => {
                      const v = parseInt(e.target.value || "0", 10);
                      if (v !== ticket.time_spent_minutes) update({ time_spent_minutes: v });
                    }}
                  />
                </Field>
                <Field label="Équipement lié">
                  <Select value={ticket.equipment_id || "__none"} onValueChange={(v) => update({ equipment_id: v === "__none" ? null : v })}>
                    <SelectTrigger data-testid="equipment-select"><SelectValue placeholder="Aucun"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">Aucun équipement</SelectItem>
                      {equipmentList.map((e) => (
                        <SelectItem key={e.id} value={e.id}>{e.name} ({e.type})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {ticket.equipment_name && (
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><HardDrives size={12}/> {ticket.equipment_name}</p>
                  )}
                </Field>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      {ticket?.client_id && (
        <UserInfoPopover
          userId={ticket.client_id}
          userName={ticket.client_name}
          open={clientPopoverOpen}
          onClose={() => setClientPopoverOpen(false)}
        />
      )}
    </div>
  );
}

function Row({ label, value, icon }) {
  return (
    <div>
      <div className="label-caps flex items-center gap-2">{icon}{label}</div>
      <div className="mt-1">{value}</div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <div className="label-caps mb-1">{label}</div>
      {children}
    </div>
  );
}

function labelForAction(a) {
  return ({
    created: "Ticket créé",
    status_changed: "Statut modifié",
    priority_changed: "Priorité modifiée",
    assigned: "Assigné",
    unassigned: "Désassigné",
  })[a] || a;
}
